import { useState, useRef, useCallback, useEffect } from 'react'
import { transcribeVoiceClip } from './api'

export type VoiceState = 'idle' | 'recording' | 'processing'

interface UseChatVoiceInputResult {
  isSupported: boolean
  state: VoiceState
  elapsedSeconds: number
  audioLevel: number
  error: string | null
  start: () => Promise<void>
  stop: () => Promise<string | null>
  cancel: () => void
}

function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const type of types) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

export function useChatVoiceInput(): UseChatVoiceInputResult {
  const [isSupported] = useState(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return false
    return !!getSupportedMimeType()
  })
  const [state, setState] = useState<VoiceState>('idle')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [audioLevel, setAudioLevel] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(0)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const levelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mimeTypeRef = useRef<string>('')
  // Resolve the stop() promise once the blob is ready
  const stopResolveRef = useRef<((blob: Blob) => void) | null>(null)

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (levelIntervalRef.current) { clearInterval(levelIntervalRef.current); levelIntervalRef.current = null }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop()
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
    mediaRecorderRef.current = null
    chunksRef.current = []
    setAudioLevel(0)
    setElapsedSeconds(0)
  }, [])

  const start = useCallback(async () => {
    setError(null)
    const mimeType = getSupportedMimeType()
    if (!mimeType) {
      setError('Audio recording not supported')
      return
    }
    mimeTypeRef.current = mimeType

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setError('Microphone access denied')
      return
    }
    streamRef.current = stream

    // AudioContext + AnalyserNode for level monitoring
    const audioCtx = new AudioContext()
    audioCtxRef.current = audioCtx
    const source = audioCtx.createMediaStreamSource(stream)
    const analyser = audioCtx.createAnalyser()
    analyser.fftSize = 256
    source.connect(analyser)
    analyserRef.current = analyser

    // Sample audio level at ~10fps
    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    levelIntervalRef.current = setInterval(() => {
      analyser.getByteFrequencyData(dataArray)
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) sum += dataArray[i]!
      setAudioLevel(sum / dataArray.length / 255)
    }, 100)

    // MediaRecorder
    chunksRef.current = []
    const recorder = new MediaRecorder(stream, { mimeType })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType })
      if (stopResolveRef.current) {
        stopResolveRef.current(blob)
        stopResolveRef.current = null
      }
    }
    mediaRecorderRef.current = recorder
    recorder.start(1000) // collect chunks every second

    // Elapsed timer
    startTimeRef.current = Date.now()
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000))
    }, 1000)

    setState('recording')
  }, [])

  const stop = useCallback(async (): Promise<string | null> => {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state === 'inactive') {
      cleanup()
      setState('idle')
      return null
    }

    // Wait for the recorder to finalize the blob
    const blob = await new Promise<Blob>((resolve) => {
      stopResolveRef.current = resolve
      recorder.stop()
    })

    // Stop timer and level monitoring, release stream
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (levelIntervalRef.current) { clearInterval(levelIntervalRef.current); levelIntervalRef.current = null }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop()
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
    mediaRecorderRef.current = null
    setAudioLevel(0)

    if (blob.size === 0) {
      cleanup()
      setState('idle')
      return null
    }

    setState('processing')

    try {
      const result = await transcribeVoiceClip(blob)
      setState('idle')
      setElapsedSeconds(0)
      chunksRef.current = []
      return result.message || null
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transcription failed')
      setState('idle')
      setElapsedSeconds(0)
      chunksRef.current = []
      return null
    }
  }, [cleanup])

  const cancel = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
    cleanup()
    setState('idle')
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') recorder.stop()
      cleanup()
    }
  }, [cleanup])

  return { isSupported, state, elapsedSeconds, audioLevel, error, start, stop, cancel }
}
